import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { File, FileFilled } from '@factorialco/f0-react/icons/app'
import {
  DetailsItemsList,
  DetailsItemType,
  OneEmptyState,
  ResourceHeader,
} from '@factorialco/f0-react/dist/experimental'
import {
  AtsVacancyDetailDocument,
  AtsVacancyDetailPersonFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  getDeadlineIcon,
  getDaysOpen,
  getStatusConfig,
  buildDetailAvatarListItem,
} from 'modules/ats/lib/utils/vacancies'

type MetadataItem =
  | { label: string; value: { type: 'text'; content: string } }
  | { label: string; value: { type: 'date'; formattedDate: string; icon?: 'warning' | 'critical' } }

const getVacancyTypeLabel = (vacancyType: string | null | undefined): string => {
  if (vacancyType === 'new_position') return i18n.t('ats.vacancies.table.vacancy_type.new_position')
  if (vacancyType === 'backfill') return i18n.t('ats.vacancies.table.vacancy_type.backfill')
  return '-'
}

const buildPersonItem = (
  person: AtsVacancyDetailPersonFragment | null | undefined,
  title: string,
  href?: string
): DetailsItemType => {
  if (!person)
    return {
      title,
      content: { type: 'item', text: '-' },
    }

  return {
    title,
    content: {
      ...(href && { action: { type: 'navigate' as const, href } }),
      type: 'person',
      firstName: person.firstName ?? '',
      lastName: person.lastName ?? '',
      avatarUrl: buildUrl(person.avatar?.url ?? ''),
    },
  }
}

const VacancyDetailPage = () => {
  const { vacancy_id } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data, loading } = useQuery(AtsVacancyDetailDocument, {
    variables: { id: vacancy_id ?? '' },
  })

  const vacancy = data?.ats.vacancy
  const statusConfig = getStatusConfig(vacancy?.status ?? '')

  const metadata = useMemo((): MetadataItem[] => {
    if (!vacancy) return []

    const items: MetadataItem[] = []

    items.push({
      label: i18n.t('ats.vacancies.table.columns.deadline'),
      value: {
        type: 'date',
        formattedDate: convertDate(vacancy.deadline ?? '', 'backend', 'ui_abbreviated_month_year'),
        icon: getDeadlineIcon(vacancy.deadline ?? ''),
      },
    })

    return items
  }, [vacancy])

  const details = useMemo((): DetailsItemType[] => {
    if (!vacancy) return []

    const items: DetailsItemType[] = []

    items.push(
      buildDetailAvatarListItem(vacancy.recruiters, i18n.t('ats.vacancies.table.columns.recruiter'))
    )

    items.push(
      buildDetailAvatarListItem(
        vacancy.atsVacancyHiringManagers,
        i18n.t('ats.vacancies.table.columns.hiring_managers')
      )
    )

    const lineManagerHref = vacancy.lineManager
      ? navigation.employees.id.fullPath({ id: vacancy.lineManager.id })
      : undefined
    items.push(
      buildPersonItem(
        vacancy.lineManager,
        i18n.t('ats.vacancies.table.columns.line_manager'),
        lineManagerHref
      )
    )

    const jobOpeningContent = vacancy.jobPosting
      ? {
          type: 'item' as const,
          text: vacancy.jobPosting.title,
          action: {
            type: 'navigate' as const,
            href: navigation.ats.jobs.jobsId.applications.fullPath({
              jobId: vacancy.jobPosting.id,
            }),
          },
        }
      : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.table.columns.job_opening'),
      content: jobOpeningContent,
    })

    const vacancyTypeContent = vacancy.vacancyType
      ? {
          type: 'dot-tag' as const,
          text: getVacancyTypeLabel(vacancy.vacancyType),
          color:
            vacancy.vacancyType === 'new_position' ? ('flubber' as const) : ('purple' as const),
        }
      : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.table.columns.vacancy_type'),
      content: vacancyTypeContent,
    })

    const employeeItem = buildPersonItem(
      vacancy.employee,
      i18n.t('ats.vacancies.table.columns.candidate_employee')
    )
    items.push(employeeItem)

    const daysOpen = getDaysOpen(vacancy.processStartedAt)
    items.push({
      title: i18n.t('ats.vacancies.table.columns.days_open'),
      content: { type: 'item', text: daysOpen !== null ? `${daysOpen}` : '-' },
    })

    const roleContent = vacancy.jobCatalogTreeRole?.name
      ? { type: 'item' as const, text: vacancy.jobCatalogTreeRole.name }
      : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.detail.role_label'),
      content: roleContent,
    })

    const levelContent = vacancy.jobCatalogTreeLevel?.name
      ? { type: 'item' as const, text: vacancy.jobCatalogTreeLevel.name }
      : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.detail.level_label'),
      content: levelContent,
    })

    const teamContent = vacancy.team?.name
      ? [{ type: 'team' as const, name: vacancy.team.name }]
      : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.table.columns.team'),
      content: teamContent,
    })

    const marketContent =
      vacancy.location?.name != null
        ? { type: 'item' as const, text: vacancy.location.name }
        : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.table.columns.market'),
      content: marketContent,
    })

    items.push({
      title: i18n.t('ats.vacancies.table.columns.origin'),
      content: {
        type: 'item' as const,
        text:
          vacancy.originType === 'requisition'
            ? i18n.t('ats.vacancies.table.origin_type.requisition')
            : i18n.t('ats.vacancies.table.origin_type.manual'),
        icon: vacancy.originType === 'requisition' ? FileFilled : File,
      },
    })

    return items
  }, [vacancy, navigation])

  const handleClose = () => navigate(navigation.ats.hiringPlan.vacancies.fullPath())

  return (
    <F0Dialog position="right" isOpen onClose={handleClose}>
      {loading && (
        <Box padding="s40" width="full" alignItems="center" flexGrow>
          <Spinner />
        </Box>
      )}
      {!loading && !vacancy && (
        <OneEmptyState
          variant="warning"
          title={i18n.t('ats.vacancies.detail.could_not_load_data')}
        />
      )}
      {!loading && vacancy && (
        <Box gap="s24" flexDirection="column">
          <ResourceHeader
            title={vacancy?.name ?? ''}
            status={
              statusConfig
                ? {
                    label: statusConfig.label,
                    text: statusConfig.label,
                    variant: statusConfig.status,
                  }
                : undefined
            }
            metadata={metadata}
          />
          <DetailsItemsList details={details} tableView />
        </Box>
      )}
    </F0Dialog>
  )
}

export default VacancyDetailPage
